import React, { useCallback, useEffect, useMemo, useState, ComponentProps } from "react";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import styles from "../country.module.scss";
import {
  PartnerNotice,
  BlogCard,
  CountryHero,
  ImagePullout,
  ReviewsList,
  QuietLinks,
  HtmlRenderBlock,
  QuietLinkProps,
  CardLayout,
  TripIdeaCard,
  Loader,
} from "@travellocal/ui";
import classNames from "classnames";
import { Filters } from "../../../utils/types";
import { getStaticPageContent, Page } from "components";
import serverSideTranslations from "utils/serverSideTranslations";
import { GetStaticPropsContext, InferGetStaticPropsType } from "next";
import { useRouter } from "next/router";
import {
  getCountriesWithPublishedTrips,
  getFiltersByCountrySlug,
  getCountryByCountrySlug,
  getTripsByCountrySlugAndFilters,
} from "utils/api/site2";
import {
  budgetFilterProps,
  countryColorFallback,
  inputPropsBudget,
  uniqueKey,
} from "utils/CountryPage/components/constants";
import { getCountry } from "utils/api/cms";
import PlanYourTrip from "utils/CountryPage/components/PlanYourTrip";
import { useAddToDataLayer } from "utils/hooks/use-add-to-datalayer";
import { currentEnvironment } from "utils/current-environment";
import { currentMarket } from "utils/current-market";
import { translateRoute } from "utils/localisedRouteHelpers";
import { site1Api, isLocale, Locale } from "@travellocal/utils";
import dynamic from "next/dynamic";
import useSWRMutation from "swr/mutation";
import yearsForCountryTitle from "utils/yearsForCountryTitle";

const SubscribeWrapper = dynamic(
  () => import("@travellocal/ui").then((mod) => mod.SubscribeWrapper),
  { ssr: false }
);

const MobileFilters = dynamic(() => import("../../../utils/CountryPage/components/mobileFilters"), {
  ssr: false,
});

const DesktopFilters = dynamic(
  () => import("../../../utils/CountryPage/components/desktopFilters"),
  { ssr: false }
);

const NoTripsMessage = dynamic(
  () => import("../../../utils/CountryPage/components/no-trips-message"),
  { ssr: false }
);

interface Country {
  id: string;
  name: string;
  slug: string;
  internalKey: string;
  countryCode: string;
  href?: string;
  countryProfileForRequestLocale?: CountryProfile;
}

interface CountryProfile {
  publishedAgents?: Agent[];
}

interface User {
  publicName?: string;
  photo?: {
    filename: string;
  };
}

interface Agent {
  user?: User;
}

const fetcher = (
  url,
  {
    arg,
  }: {
    arg: {
      country: string;
      selectedValues: string;
      offset: number;
    };
  }
) =>
  fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(arg),
  }).then(async (res) => {
    const jsonData = await res.json();

    return {
      matchingTripsForCurrentPage: jsonData?.dataRequestForm?.matchingTripsForCurrentPage,
      hasNextPage: jsonData?.dataRequestForm?.hasNextPage,
    };
  });

type GetStaticPropsOutput = InferGetStaticPropsType<typeof getStaticProps>;

type CountryProps = Omit<
  GetStaticPropsOutput,
  "country" | "alternatePages" | "countryParam"
> & { subscribeResponse: GetStaticPropsOutput["pageProps"]["subscribeResponse"] };

const getReviewsListProps = ({
  locale,
  countryCode,
  trustpilotReviewSummary
}: {
  locale: Locale;
  countryCode: string;
  trustpilotReviewSummary: Awaited<ReturnType<typeof getCountryByCountrySlug>>['tripBookingTrustpilotReviewSummaryForCountry'];
}): ComponentProps<typeof ReviewsList> => {
  const reviewForCurrentLocale = trustpilotReviewSummary.find(summary => summary.locale === locale);
  const countryHasReviewsInCurrentLocale = reviewForCurrentLocale?.summary.numberOfFiveStars > 0 || reviewForCurrentLocale?.summary.numberOfFourStars > 0;

  if(countryHasReviewsInCurrentLocale){
    // has 4,5 stars reviews in current locale
    return {
      locale,
      trustpilotFilters: { 'data-tags': `ctr-${countryCode.toLowerCase()}` }
    }
    
  } if(!countryHasReviewsInCurrentLocale){
    // no 4,5 stars reviews in current locale
    const reviewForEN = trustpilotReviewSummary.find(summary => summary.locale === "en");
    const hasCountryReviewsInEN = reviewForEN?.summary.numberOfFiveStars > 0 || reviewForEN?.summary.numberOfFourStars > 0;

    if(hasCountryReviewsInEN){
      // has 4,5 stars reviews in EN
      return {
        locale: 'en',
        trustpilotFilters: { 'data-tags': `ctr-${countryCode.toLowerCase()}` }
      }
    }

    // no 4,5 stars reviews in EN or current locale
    return {
      locale,
      trustpilotFilters: {}
    }
  }
  
  
}

export const Country = ({
  countryProps,
  localisedCountryName,
  filters,
  ltos,
  planYourTrip,
  matchingTrips,
  regions,
  quietLinks,
  internalKey,
  countryCode,
  subscribeResponse,
  pageProps,
  trustpilotReviewSummary
}: CountryProps) => {
  const { t } = useTranslation();
  const [isLoadingTripIdeas, setIsLoadingTripIdeas] = useState<boolean>(false);
  const { pathname, query, push } = useRouter();
  const methods = useForm();
  const isValidHexCode = (code?: string | null): boolean => {
    return code ? /^#[0-9A-F]{6}$/i.test(code) : false;
  };
  const locale = pageProps.initialAppState.locale || `en`;
  const { data: newTripIdeas, trigger: refetchTripIdeas } = useSWRMutation(
    `/api/tripFilterRequest?locale=${locale}`,
    fetcher
  );

  const [hasInitialTripIdeas, setHasInitialTripIdeas] = useState<boolean>(true);
  useEffect(() => {
    setHasInitialTripIdeas((matchingTrips.matchingTripsForCurrentPage || []).length > 0);
  }, [matchingTrips.matchingTripsForCurrentPage]);

  const tripIdeas = newTripIdeas ?? matchingTrips;

  const countryPropsData = useMemo(
    () => ({
      introGroup: {
        intro: countryProps.components?.introgroup?.intro,
        subheading: countryProps.components?.introgroup?.subheading,
      },
      partnerNotice1: {
        content: t(
          "web:CountryPage_PartnerNotice1_content",
          "We’re here in {{country}} ready to plan your trip with you, just the way you like it.",
          {
            country: localisedCountryName,
          }
        ),
        heading: t(
          "web:CountryPage_PartnerNotice1_heading",
          "We’ve put together some ideas for you"
        ),
        stamp: true,
      },
      countryHero: {
        video: countryProps.countryherovideo,
        src: countryProps.countryheroimage?.sourceUrl,
      },
      partnerNotice2: {
        content: countryProps.components?.partnernoticeContent,
        heading: countryProps.components?.partnernoticeHeading,
        stamp: countryProps.components?.partnernoticeStamp,
      },
      imagePullout: {
        src: countryProps.components?.imagepulloutImage?.sourceUrl,
      },
    }),
    [countryProps, t, localisedCountryName]
  );

  const countryHeroProps = useMemo(
    () => ({
      scrolltoID: "sharedTrips",
      visitCountryLabel: t("web:CountryPage_Intro_visit", "Visit {{country}}", {
        country: localisedCountryName,
      }),
      craftYourUniqueTripLabel: t(
        "web:CountryPage_Intro_craft",
        "Craft your unique {{country}} trip with experienced local experts",
        {
          country: localisedCountryName,
        }
      ),
      tripIdeasLabel: t("web:CountryPage_tripIdeas", "Trip ideas"),
      planYourTriplabel: t("web:CountryPage_planYourTrip_ctaText", "Plan your trip"),
      breadcrumbsItemsLabels: [
        {
          label: t("common:pageName_index", "Home"),
          href: t("routes:index", "/"),
        },
        {
          label: t("common:pageName_destinations", "Destinations"),
          href: t("routes:destinations", "/destinations"),
        },
        ...(regions ? [{
          label: regions.pageTitle,
          href: translateRoute(locale, "regionProfileBySlug", {
            slug: regions.urlFragment,
          }, {
            includeHost: false
          }),
        }] : []),
        { label: localisedCountryName },
      ],
      country: localisedCountryName,
      value: planYourTrip,
      color: isValidHexCode(countryProps.countrycolour)
        ? countryProps.countrycolour
        : countryColorFallback,
      heroImage: {
        src: countryPropsData?.countryHero?.src ?? process.env.FALLBACK_IMAGE_INTENT_PAGE,
        alt: t("web:CountryPage_Intro_visit", "Visit {{country}}", {
          country: localisedCountryName,
        }),
      },
      video: {
        webm:
          countryPropsData?.countryHero?.video && countryPropsData.countryHero.video.includes(".webm")
            ? countryPropsData.countryHero?.video
            : null,
        mp4:
          countryPropsData?.countryHero?.video && countryPropsData.countryHero.video.includes(".mp4")
            ? countryPropsData.countryHero?.video
            : null,
      },
    }),
    [localisedCountryName, countryPropsData, t, locale]
  );

  const getCurrencySymbol = (uniqueUrlKey) => {
    if (filters) {
      for (const filter of filters) {
        if (filter.uniqueUrlKey === uniqueUrlKey) {
          return filter.currencySymbol;
        }
      }
    }
    return null;
  };

  const loadTripIdeas = useCallback(
    async ({ query }) => {
      try {
        const filterQuery = Object.values(uniqueKey).reduce(
          (f, k) => ({ ...f, [k]: (query[k] as string)?.split(",") }),
          {}
        );

        refetchTripIdeas({
          country: query.country,
          selectedValues: JSON.stringify(filterQuery),
          offset: tripIdeas.matchingTripsForCurrentPage.length + 8,
        });
      } catch (error) {
        console.error("Error fetching initial trip request:", error);
      }
    },
    [tripIdeas.matchingTripsForCurrentPage.length, refetchTripIdeas]
  );

  const onFilterClick = useCallback(
    (ref: HTMLFormElement) => {
      const formData = new FormData(ref);
      const searchParam = new URLSearchParams();
      const budgetFilterValue = [];

      // collects pair value when "budget-filter*" is found
      formData.forEach((value, key) => {
        if (key === `${inputPropsBudget.name}.0` || key === `${inputPropsBudget.name}.1`) {
          budgetFilterValue.push(value);
        }
      });

      // appends collected pair values to uniqueKey.budget constant
      // so it can be used in useRouter's push function
      if (
        !(
          `${budgetFilterValue[0]}` === `${budgetFilterProps.initialSteps[0]}` &&
          `${budgetFilterValue[1]}` === `${budgetFilterProps.initialSteps[1]}`
        )
      ) {
        searchParam.append(uniqueKey.budget, `${[budgetFilterValue.join("_")]}`);
      }

      for (const filter of Object.values(uniqueKey)) {
        const value = formData.getAll(filter);
        if (value.length > 0) searchParam.append(filter, value.join(","));
      }

      push(
        {
          pathname,
          query: {
            country: query.country,
            ...Object.fromEntries(searchParam),
          },
        },
        t("routes:destinationsByCountry", "/destinations/{{country}}", {
          country: query.country,
        }) +
        "?" +
        searchParam.toString(),
        { shallow: true, scroll: false }
      ).then(() => {
        loadTripIdeas({
          query: {
            country: query.country,
            ...Object.fromEntries(searchParam),
          },
        });
      });
    },
    [pathname, query, loadTripIdeas, push, t]
  );

  const isIntro = !!(countryPropsData?.introGroup?.intro && countryPropsData?.introGroup?.subheading);
  const isPartnerNotice1Group = !!(isIntro && countryPropsData?.partnerNotice1?.content);
  const isPartnerNotice2Group = !!countryPropsData?.partnerNotice2?.content;
  const isReviewsList = !!countryProps.components?.reviewslistReviews;
  const blogCard1 = countryProps.components?.articlesArticles?.[0]?.article;
  const blogCard2 = countryProps.components?.articlesArticles?.[1]?.article;
  const isBlogCards = !!blogCard1 || !!blogCard2;

  const filterLabels = {
    experiences: t("web:CountryPage_Filters_Experiences", "Experiences"),
    budget: t("web:CountryPage_Filters_Budget", "Budget"),
    length: t("web:CountryPage_Filters_Length", "Length"),
    travelStyle: t("web:CountryPage_Filters_Travel_Style", "Travel Style"),
    regions: t("web:CountryPage_Filters_Regions", "Regions"),
    accommodation: t("web:CountryPage_Filters_Accommodation", "Accommodation"),
  };

  const clearFilters = () => {
    push(
      {
        pathname,
        query: {
          country: query.country,
        },
      },
      undefined,
      { shallow: true }
    ).then(() => {
      loadTripIdeas({ query: { country: query.country } });
    });
  };

  return (
    <div className={styles.countryContainer} id="modal">
      <>
        <section
          className={classNames(styles.hero, {
            [styles.noBackground]: !isIntro,
          })}>
          <CountryHero {...countryHeroProps} hideTripIdeasButton={!hasInitialTripIdeas} />
        </section>

        {isIntro && (
          <section className={classNames("intro", "py-7", styles.intro)}>
            <div className="container">
              <div className="columns is-marginless">
                <div className="column is-offset-1 is-3-widescreen">
                  <h2 className="title is-size-3 ">{countryPropsData?.introGroup?.subheading}</h2>
                </div>
                <div className="column is-offset-1 is-6-widescreen">
                  <h3 className="is-size-5">
                    <HtmlRenderBlock htmlString={countryPropsData?.introGroup?.intro} />
                  </h3>
                </div>
              </div>
            </div>
          </section>
        )}

        {hasInitialTripIdeas && isPartnerNotice1Group && (
          <section className={styles.partnerNotice}>
            <div className={classNames("container", styles.partnerNotice1)}>
              <div className="columns is-mobile is-marginless is-centered">
                <div className="column is-paddingless is-7-widescreen">
                  <PartnerNotice
                    content={t(
                      "web:CountryPage_PartnerNotice1_content",
                      // eslint-disable-next-line max-len
                      "We’re here in {{country}} ready to plan your trip with you, just the way you like it.",
                      {
                        country: localisedCountryName,
                      }
                    )}
                    heading={t(
                      "web:CountryPage_PartnerNotice1_heading",
                      "We’ve put together some ideas for you"
                    )}
                    items={ltos
                      ?.filter(lto => lto?.user?.photo?.filename)
                      .slice(0, 3)
                      .map((lto) => ({
                        url: `${process.env.ASSETS_DOMAIN}/150x150/${lto.user.photo.filename}`,
                        alt: lto.user.publicName,
                      }))}
                    stamp={countryPropsData?.partnerNotice1?.stamp === true ? "on" : "off"}
                  />
                </div>
              </div>
            </div>
          </section>
        )}

        <div id="sharedTrips">
          {hasInitialTripIdeas && (
            <FormProvider {...methods}>
              <div className={styles.showFiltersMobile}>
                <MobileFilters
                  onFilterClick={onFilterClick}
                  filters={filters as Filters}
                  methods={methods}
                  countryName={countryHeroProps.country?.toLowerCase()}
                  budgetSliderCurrency={getCurrencySymbol("p")}
                  labels={filterLabels}
                />
              </div>
              <div
                className={classNames(styles.showFiltersDesktop, {
                  [styles.findIdeasWrapper]: !!tripIdeas?.hasNextPage,
                })}>
                <DesktopFilters
                  onFilterClick={onFilterClick}
                  filters={filters}
                  methods={methods}
                  budgetSliderCurrency={getCurrencySymbol("p")}
                  labels={filterLabels}
                />
              </div>
            </FormProvider>
          )}
        </div>

        <div className="container">
          {Array.isArray(tripIdeas?.matchingTripsForCurrentPage) &&
            tripIdeas?.matchingTripsForCurrentPage.length > 0 ? (
            <CardLayout layout="grid">
              {tripIdeas?.matchingTripsForCurrentPage.map(
                ({
                  id,
                  title,
                  mainPhotoURL,
                  amountOfTripDays,
                  tripPath,
                  startingPricePerPerson,
                  currency,
                  linkToThreeStepRequestForm,
                }) => {
                  return (
                    <TripIdeaCard
                      key={id}
                      title={title}
                      heroImageUrl={mainPhotoURL}
                      heroImageMeta=""
                      numberOfDays={amountOfTripDays}
                      url={tripPath}
                      pricePerPerson={{
                        price: startingPricePerPerson ?? 0,
                        currency: {
                          availableToCustomers: true,
                          symbol: currency?.symbol,
                          code: currency?.abbreviation,
                        } as site1Api.Currency,
                      }}
                      cardHref={tripPath}
                      cardTarget="_self"
                      customiseAndRequestHref={linkToThreeStepRequestForm}
                      customiseAndRequestTarget="_self"
                      customiseAndRequestLabel={t(
                        "web:CountryPage_TripIdeaCard_CustomiseAndRequestLabel",
                        "Customize and request"
                      )}
                    />
                  );
                }
              )}
            </CardLayout>
          ) : (
            <NoTripsMessage
              clearFilters={hasInitialTripIdeas ? clearFilters : undefined}
              className="my-6 is-centered"
              hasInitialTripIdeas={hasInitialTripIdeas}
            />
          )}
        </div>

        {!isLoadingTripIdeas && !!tripIdeas?.hasNextPage && (
          <div className={styles.loadMoreWrapper}>
            <button
              className="button is-ghost is-justify-content-center"
              type="button"
              onClick={async () => {
                setIsLoadingTripIdeas(true);
                await loadTripIdeas({ query });
                setIsLoadingTripIdeas(false);
              }}>
              {t("CountryPage_LoadMore", "Load more")}
            </button>
          </div>
        )}

        {isLoadingTripIdeas && (
          <div>
            <Loader className="is-justify-content-center" />
          </div>
        )}

        {hasInitialTripIdeas && (
          <div>
            <PlanYourTrip
              country={countryHeroProps.country}
              styles={styles}
              requestUrl={planYourTrip}
            />
          </div>
        )}


        <section
          className={classNames(styles.reviews, "pt-7", {
            "pb-7": !isBlogCards,
          })}>

          <ReviewsList
            {...getReviewsListProps({
              locale,
              countryCode,
              trustpilotReviewSummary
            })}
          />

        </section>


        {isBlogCards && (
          <section className={classNames(styles.articles, "pt-7 pb-8")}>
            <div className="container">
              <div className="columns is-centered">
                {blogCard1 && (
                  <div className="column is-12-mobile is-5-widescreen">
                    {blogCard1?.date &&
                      blogCard1?.featuredImage?.node?.sourceUrl &&
                      blogCard1?.title &&
                      blogCard1?.link && (
                        <BlogCard
                          date={blogCard1.date ? new Date(blogCard1.date) : undefined}
                          image={blogCard1.featuredImage.node.sourceUrl}
                          size="large"
                          title={blogCard1.title}
                          url={t("routes:articleBySlug", {
                            slug: blogCard1.meta.urlFragment,
                          })}
                          target="_self"
                        />
                      )}
                  </div>
                )}
                {blogCard2 && (
                  <div className="column is-12-mobile is-5-widescreen">
                    {blogCard2?.date &&
                      blogCard2?.featuredImage?.node?.sourceUrl &&
                      blogCard2?.title &&
                      blogCard2?.link && (
                        <BlogCard
                          date={blogCard2.date ? new Date(blogCard2.date) : undefined}
                          image={blogCard2.featuredImage.node.sourceUrl}
                          size="large"
                          title={blogCard2.title}
                          url={t("routes:articleBySlug", {
                            slug: blogCard2.meta.urlFragment,
                          })}
                          target="_self"
                        />
                      )}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        <section
          className={classNames(styles.subscribeContainer, "pb-7", {
            [styles.noBackground]: !isBlogCards && !isReviewsList,
          })}>
          <SubscribeWrapper
            subscribeClassName="container"
            title={subscribeResponse.subscribeText || undefined}
            bodyText={subscribeResponse.subscribeBodyText || undefined}
            locale={locale}
            signupSource="destination_page"
            country={internalKey}
            dataTrackingIds={{
              firstName: "frm.subscribe.first_name",
              email: "frm.subscribe.email",
            }}
          />
        </section>

        {isPartnerNotice2Group && (
          <section
            className={classNames(styles.partnerNotice, "pb-6 mb-6", {
              [styles.noBackground]: !isBlogCards && !isReviewsList,
            })}>
            <div className="container">
              <div className="columns is-mobile is-centered">
                <div className="column is-10-widescreen">
                  <PartnerNotice
                    content={countryPropsData?.partnerNotice2?.content}
                    heading={countryPropsData?.partnerNotice2?.heading || ""}
                    items={ltos?.slice(0, 3).map((lto) => ({
                      url: `${process.env.ASSETS_DOMAIN}/150x150/${lto.user.photo.filename}`,
                      alt: lto.user.publicName,
                    }))}
                    stamp={countryPropsData?.partnerNotice2?.stamp === true ? "on" : "off"}
                  />
                </div>
              </div>
            </div>
          </section>
        )}

        <section className={classNames("mb-4", { "mt-7": !isPartnerNotice2Group })}>
          <div className="container">
            <ImagePullout
              bgSrc={countryPropsData?.imagePullout?.src ?? countryHeroProps.heroImage.src}
              heading={t(
                "web:CountryPage_ImagePullout_heading",
                "We're reimagining a fairer way to visit {{country}}",
                { country: localisedCountryName }
              )}
              text={t(
                "web:CountryPage_ImagePullout_text",
                "Travel that benefits all; our environment, ecosystems, local partners, and travellers."
              )}
              callToAction={{
                target: "_self",
                title: t("web:CountryPage_ImagePullout_ctaTitle", "How it works"),
                url: t("routes:howItWorks", "/how-it-works"),
              }}
            />
          </div>
        </section>

        {quietLinks && (
          <section className={classNames(styles.quietLinks, "mb-4 has-text-centered")}>
            <QuietLinks quietLinks={quietLinks as QuietLinkProps[]} />
          </section>
        )}
      </>
    </div>
  );
};

function PageWrapper({
  pageProps,
  country,
  countryParam,
  alternatePages,
  ...rest
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const { t } = useTranslation();
  const pageTitle = t("common:pageName_country", "Visit {{country}} {{years}}", {
    country: rest.localisedCountryName,
    years: yearsForCountryTitle()
  });

  useAddToDataLayer({
    environment: currentEnvironment(), // use this with: prod, staging, dev
    page: {
      market: currentMarket(pageProps.initialAppState.locale), // NEW, I'd like to introduce this (de, en, en_GB, NOTE, NOT "en-gb")
      destinationContinent: country?.continent?.internalKey, // please use this (obtained by site 2 API)
      destinationCountry: country?.internalKey, // please use this
      language: pageProps.initialAppState.locale, // please use this (duplicate of market)
      subtype: "country lp", // please use this, it is static text
      title: pageTitle, // shouldn't be needed as same as title
      type: "category", // please use this, it is static text
    },
  });

  const canonicalUrl = translateRoute(rest.currentLocale, `destinationsByCountry`, {
    country: country?.slug || countryParam,
  });

  return (
    <Page
      title={pageTitle}
      alternatePages={alternatePages}
      meta={{
        title: rest.meta.searchTitle,
        description: rest.meta.searchDescription,
        imageSrc: rest.meta.searchImage?.sourceUrl,
        locale: rest.currentLocale,
        canonicalUrl,
      }}
      {...pageProps}>
      <Country {...rest} subscribeResponse={pageProps.subscribeResponse} pageProps={pageProps} />
    </Page>
  );
}

export const getStaticProps = async (context: GetStaticPropsContext) => {
  if (!isLocale(context.locale)) {
    return {
      notFound: true,
    };
  }

  const pageProps = await getStaticPageContent(context);
  const currentLocale =
    pageProps.initialAppState.locale ?? pageProps.initialAppState.detectedLocale;

  const countryParam = Array.isArray(context.params.country)
    ? context.params.country[0].toLowerCase()
    : context.params.country.toLowerCase();
  const dataRequestForm = await getCountry(countryParam, [currentLocale], `get country`);

  if (!dataRequestForm?.nodes?.length) {
    return {
      notFound: true,
    };
  }

  const response = await getFiltersByCountrySlug(currentLocale, countryParam);

  // TODO - temp solution, improve later with api call
  const defaultPath = (() => {
    switch (currentLocale) {
      case "de":
        return `/${currentLocale}/${countryParam}/reisen/detaillierte-anfrage/start`;
      default:
        return `/${currentLocale}/${countryParam}/tours/detailed-request-form/start`;
    }
  })();

  const dataTripRequest = await getCountryByCountrySlug(currentLocale, countryParam);

  if (!dataTripRequest) {
    return {
      notFound: true,
    };
  }

  const countriesToTranslate = dataRequestForm?.nodes[0]?.countries.nodes[0]?.countryProfiles.nodes;
  const alternatePages = (countriesToTranslate || []).map((country) => {
    const locale = country.locales.nodes[0].name.toLowerCase();
    return {
      locale,
      url: translateRoute(locale, "destinationsByCountry", {
        country: country.meta.urlFragment,
      }),
    };
  });

  const country = pageProps.countries.find((country) => country.slug === countryParam);

  const initialTripIdeas = country?.slug
    ? await getTripsByCountrySlugAndFilters(currentLocale, country.slug, JSON.stringify({}), 0, 8)
    : null;

  return {
    props: {
      ...(await serverSideTranslations(currentLocale)),
      pageProps,
      currentLocale,
      filters: response?.tripFilterElements ?? null,
      localisedCountryName: dataTripRequest.name,
      quietLinks: dataRequestForm?.globalContent[0]?.globalContent?.quietlinksLinks,
      countryProps: dataRequestForm?.nodes[0]?.country,
      planYourTrip: defaultPath,
      country: country ?? null,
      ltos:
        pageProps.countries.find((country) => country.slug === countryParam)
          ?.countryProfileForRequestLocale?.publishedAgents ?? [],
      internalKey: dataTripRequest.internalKey,
      countryCode: dataTripRequest.code,
      trustpilotReviewSummary: dataTripRequest.tripBookingTrustpilotReviewSummaryForCountry,
      alternatePages,
      meta: dataRequestForm.nodes[0].meta,
      regions: dataRequestForm.nodes[0].regions?.nodes[0]?.slug?.nodes[0]?.meta ?? null,
      countryParam,
      matchingTrips: {
        matchingTripsForCurrentPage: initialTripIdeas
          ? initialTripIdeas?.matchingTripsForCurrentPage
          : [],
        hasNextPage:
          (initialTripIdeas?.matchingTripsForCurrentPage || []).length > 0 &&
          initialTripIdeas?.hasNextPage,
      },
    },
  };
};

export async function getStaticPaths({ locales }) {
  // initialize country slug array
  const countryPaths = [];

  /* get all available countries for each locale  */
  for (const locale of locales) {
    if (!isLocale(locale)) {
      continue;
    }

    const countries = await getCountriesWithPublishedTrips(locale);

    // add country slugs to array
    countries.forEach((country) => {
      countryPaths.push({
        params: { locale, country: country.slug },
      });
    });
  }

  // We'll pre-render only these paths at build time.
  // {fallback: 'blocking' } will server-render pages
  // on-demand if the path doesn't exist.
  return { paths: countryPaths, fallback: "blocking" };
}

export default PageWrapper;
